// ============================================================
// OPPOSED TESTS - two combatants roll, one outcome.
//
// Feint, Knock-Down, Grapple and their kin are decided by comparing
// Degrees of Success, not by a lone d100. Both halves reuse the existing
// single-test rollers, so difficulty, condition penalties, the Unnatural
// house rule and the extraMod channel all apply without a second
// implementation - this module only ORDERS the rolls and JUDGES them.
//
// The actor rolls FIRST, then the defender. That order is a contract:
// the E2E harness feeds a seeded dice sequence, and a swapped order
// silently shifts every later roll in the test.
//
// Pure. Dice injected via engine/dice. Zero React dependency.
// ============================================================
import type { Character, CharacteristicId, SkillId } from './character/types';
import type { Target } from '../types';
import type { StructuredNarrativeEntry, ModifierDetail } from './structuredNarrative';
import {
  rollCharacteristicTest,
  rollSkillTest,
  rollPlainTargetTest,
  calcPlainTargetTestBase,
  SKILL_TEST_DEFS,
  type TestResult,
  type UnnaturalTestOpts,
  type PlainTargetTestId,
} from './characteristicTests';
import type { UnnaturalDosMode } from './unnaturalDos';

/**
 * One side of an opposed test. `mod` is a situational bonus/penalty on
 * that side's target number; `unnatural` threads the house rule exactly
 * as the single-test API does.
 */
/**
 * Degrees a side brings with it, before any dice (v8.298.0). The
 * Grapple size rule is the first: "+1 Degree of Success per size
 * difference in all Opposed Tests".
 *
 * A bonus counts ONLY toward the comparison. It never touches
 * `TestResult.dos`, which `buildOpposedNarrative` prints verbatim - a
 * log claiming degrees the dice never gave would be exactly the quiet
 * drift the anti-drift rule exists to stop. And a side that FAILED
 * carries degrees of failure, not success, so its bonus is moot.
 */
interface DosBonus {
  dosBonus?: number;
}

export type OpposedSide = DosBonus &
  (
    | {
        kind: 'characteristic';
        character: Character;
        charId: CharacteristicId;
        mod?: number;
        unnatural?: UnnaturalTestOpts;
      }
    | { kind: 'skill'; character: Character; skillId: SkillId; mod?: number; unnatural?: UnnaturalTestOpts }
    | { kind: 'plain'; target: Target; testId: PlainTargetTestId; mod?: number; unnatural?: UnnaturalTestOpts }
  );

export interface OpposedResult {
  actor: TestResult;
  defender: TestResult;
  winner: 'actor' | 'defender' | 'none';
  /**
   * DoS separation: winner DoS minus loser DoS when both succeeded, the
   * winner's full DoS when the other side failed, and 0 when the win
   * came from the characteristic-value tie-break (RAW: no margin).
   */
  margin: number;
  /** Set when equal DoS was settled by the higher characteristic value. */
  tieBrokenByValue?: boolean;
  /**
   * The degrees each side brought before the dice, present only when
   * somebody brought any. The caller names the reason in a note.
   */
  bonuses?: { actor: number; defender: number };
}

/**
 * What the table has ruled about Unnatural Characteristics and degrees
 * of success. Filled in HERE, once, for both sides (v8.310.10): every
 * production caller built its sides without it, so `resolveRoll` fell
 * to `'off'` and the GM's ruling - honoured on sheet tests, attack rolls
 * and defence reactions - was silently ignored by Feint, Knock-Down,
 * Stun, Manoeuvre, Grapple and both right-click opposed rolls. A side
 * that names its own `unnatural.mode` keeps it.
 */
export interface OpposedTestOptions {
  unnaturalDosMode?: UnnaturalDosMode;
}

function withMode(side: OpposedSide, mode: UnnaturalDosMode | undefined): UnnaturalTestOpts | undefined {
  if (mode === undefined) return side.unnatural;
  return { mode, ...(side.unnatural ?? {}) };
}

function rollSide(side: OpposedSide, mode: UnnaturalDosMode | undefined): TestResult {
  const mod = side.mod ?? 0;
  const unnatural = withMode(side, mode);
  if (side.kind === 'characteristic') return rollCharacteristicTest(side.character, side.charId, 0, mod, unnatural);
  if (side.kind === 'skill') return rollSkillTest(side.character, side.skillId, 0, mod, unnatural);
  return rollPlainTargetTest(side.target, side.testId, 0, mod, unnatural);
}

const governingOf = (skillId: SkillId): CharacteristicId => SKILL_TEST_DEFS.find((d) => d.id === skillId)!.governing;

/**
 * WHAT A SIDE TESTS, named without saying whose: a skill or a
 * characteristic. An opposed drum action names one per side (Hide is
 * Concealment against Awareness), and so does a called test.
 */
export type TestSubject = { kind: 'skill'; id: SkillId } | { kind: 'characteristic'; id: CharacteristicId };

/** The characteristic a subject rests on: its own, or the skill's governing one. */
export const characteristicOf = (subject: TestSubject): CharacteristicId =>
  subject.kind === 'skill' ? governingOf(subject.id) : subject.id;

/** The value that settles an equal-DoS tie: the side's raw base. */
function tieValue(side: OpposedSide): number {
  if (side.kind === 'characteristic') return side.character.characteristics[side.charId].value;
  // The skill's OWN governing characteristic. This read Agility for every skill until 2026-10-05,
  // when Scrutiny against Deceive first asked.
  if (side.kind === 'skill') return side.character.characteristics[governingOf(side.skillId)].value;
  return calcPlainTargetTestBase(side.target, side.testId);
}

type Verdict = Pick<OpposedResult, 'winner' | 'margin' | 'tieBrokenByValue'>;

/** Both succeeded with equal degrees: the higher characteristic value wins, with no margin; equal values, nobody. */
function breakTie(actorSide: OpposedSide, defenderSide: OpposedSide): Verdict {
  const a = tieValue(actorSide);
  const d = tieValue(defenderSide);
  if (a === d) return { winner: 'none', margin: 0 };
  return { winner: a > d ? 'actor' : 'defender', margin: 0, tieBrokenByValue: true };
}

/**
 * Judge two rolled sides. `actorDos` / `defenderDos` include the bonus
 * degrees, which count toward the COMPARISON only.
 */
function judge(
  actor: TestResult,
  defender: TestResult,
  dos: { actor: number; defender: number },
  sides: { actor: OpposedSide; defender: OpposedSide },
): Verdict {
  if (actor.success !== defender.success) {
    return actor.success ? { winner: 'actor', margin: dos.actor } : { winner: 'defender', margin: dos.defender };
  }
  if (!actor.success) return { winner: 'none', margin: 0 };
  if (dos.actor === dos.defender) return breakTie(sides.actor, sides.defender);
  return { winner: dos.actor > dos.defender ? 'actor' : 'defender', margin: Math.abs(dos.actor - dos.defender) };
}

/**
 * Roll both sides and judge. Success beats failure; both successes
 * compare DoS; equal DoS falls to the higher characteristic value; a
 * value tie, or two failures, is no winner at all. The actor rolls first.
 */
export function rollOpposedTest(
  actorSide: OpposedSide,
  defenderSide: OpposedSide,
  opts: OpposedTestOptions = {},
): OpposedResult {
  const actor = rollSide(actorSide, opts.unnaturalDosMode);
  const defender = rollSide(defenderSide, opts.unnaturalDosMode);

  // Bonus degrees count toward the COMPARISON only - never written back
  // onto the TestResult the narrative prints (v8.298.0).
  const actorBonus = actorSide.dosBonus ?? 0;
  const defenderBonus = defenderSide.dosBonus ?? 0;
  const carried = actorBonus !== 0 || defenderBonus !== 0;
  const bonuses = carried ? { bonuses: { actor: actorBonus, defender: defenderBonus } } : {};
  const dos = { actor: actor.dos + actorBonus, defender: defender.dos + defenderBonus };
  return { actor, defender, ...bonuses, ...judge(actor, defender, dos, { actor: actorSide, defender: defenderSide }) };
}

const sideSummary = (r: TestResult) => `${r.roll} vs ${r.target} (${r.success ? `${r.dos} DoS` : `${r.dof} DoF`})`;

/** "Brother Tacitus wins by 2 DoS", "... wins (higher characteristic)", "no winner". */
function verdictText(result: OpposedResult, actorName: string, defenderName: string): string {
  if (result.winner === 'none') return 'no winner';
  const who = result.winner === 'actor' ? actorName : defenderName;
  if (result.tieBrokenByValue) return `${who} wins (higher characteristic)`;
  return result.margin > 0 ? `${who} wins by ${result.margin} DoS` : `${who} wins`;
}

/** One side's modifier rows, prefixed by name: its base, and its situational modifier when it has one. */
function sideModifiers(name: string, r: TestResult): ModifierDetail[] {
  const base = { label: `${name}: ${r.label.replace(' Test', '')}`, value: r.baseValue };
  return r.customMod !== 0 ? [base, { label: `${name}: situational`, value: r.customMod }] : [base];
}

/** One side's roll row. */
const sideRoll = (name: string, r: TestResult) => ({
  label: `${name}: ${r.label}`,
  roll: r.roll,
  target: r.target,
  success: r.success,
});

/**
 * ONE log entry for the whole exchange - both rolls in `detail.rolls`
 * (the array was always plural for exactly this), both sides' modifiers
 * prefixed by name, and the verdict in the summary line.
 */
export function buildOpposedNarrative(
  result: OpposedResult,
  actorName: string,
  defenderName: string,
  actionLabel: string,
  /**
   * Extra sentences for the entry - the reason behind a `dosBonus`, an
   * action's rules reminder. NOT a ModifierDetail row: that list is in
   * test points and a bonus in degrees would read as a to-hit modifier.
   */
  notes: string[] = [],
): StructuredNarrativeEntry {
  const { actor, defender, winner } = result;
  const lines = [actor.unnaturalNote, defender.unnaturalNote, ...notes].filter((n): n is string => !!n);
  return {
    summary:
      `${actionLabel}: ${actorName} ${sideSummary(actor)} against ` +
      `${defenderName} ${sideSummary(defender)} - ${verdictText(result, actorName, defenderName)}`,
    detail: {
      rolls: [sideRoll(actorName, actor), sideRoll(defenderName, defender)],
      modifiers: [...sideModifiers(actorName, actor), ...sideModifiers(defenderName, defender)],
      ...(lines.length ? { specialNotes: lines } : {}),
    },
    outcome: winner === 'actor' ? 'hit' : 'miss',
  };
}

/**
 * The statblock's answer to "oppose my WS": the five derived axes a
 * plain Target can roll. Anything else has no opposed footing on a
 * statblock - callers surface that as a sentence, not a guess.
 */
export function plainAxisFor(charId: CharacteristicId): PlainTargetTestId | null {
  switch (charId) {
    case 'ws':
      return 'parry';
    case 'agi':
      return 'dodge';
    case 'tgh':
      return 'toughness';
    case 'str':
      return 'strength';
    case 'wp':
      return 'willpower';
    default:
      return null;
  }
}
